import React from 'react';
import { t } from '../i18n';

/**
 * Shared presentation primitives.
 *
 * Keeps Tailwind class strings in one place so screens stay readable and the
 * look stays consistent — the indigo scheme from the brief, with loading and
 * disabled states built in rather than reinvented per screen.
 */

export const Spinner = ({ label }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-12">
    <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
    <p className="text-sm text-slate-500">{label ?? t('common.loading')}</p>
  </div>
);

export const FullPageSpinner = ({ label }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50">
    <Spinner label={label} />
  </div>
);

const BUTTON_VARIANTS = {
  primary: 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500',
  secondary: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 focus:ring-slate-400',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500',
  danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
  ghost: 'text-indigo-600 hover:bg-indigo-50 focus:ring-indigo-500',
};

export const Button = ({
  variant = 'primary',
  loading = false,
  disabled = false,
  className = '',
  children,
  ...props
}) => (
  <button
    disabled={disabled || loading}
    className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold
      transition focus:outline-none focus:ring-2 focus:ring-offset-1
      disabled:cursor-not-allowed disabled:opacity-50
      ${BUTTON_VARIANTS[variant]} ${className}`}
    {...props}
  >
    {loading && (
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
    )}
    {children}
  </button>
);

export const Card = ({ title, subtitle, actions, className = '', children }) => (
  <section className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}>
    {(title || actions) && (
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
        <div>
          {title && <h2 className="text-lg font-semibold text-slate-900">{title}</h2>}
          {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex gap-2">{actions}</div>}
      </header>
    )}
    <div className="p-6">{children}</div>
  </section>
);

export const Field = ({ label, required, error, hint, children }) => (
  <label className="block">
    <span className="mb-1 block text-sm font-medium text-slate-700">
      {label}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </span>
    {children}
    {hint && !error && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
  </label>
);

const CONTROL_CLASS =
  `w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900
   placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1
   focus:ring-indigo-500 disabled:bg-slate-100 disabled:text-slate-500`;

// Full width unless the caller gives one. Appending "w-28" to "w-full" never
// narrowed a field: both set the width, and Tailwind's stylesheet lists w-full
// after w-28, so w-full always won and every short field spanned its row.
const controlClass = (className) => (/(^|\s)w-/.test(className)
  ? `${CONTROL_CLASS.replace('w-full ', '')} ${className}`
  : `${CONTROL_CLASS} ${className}`);

export const Input = ({ className = '', ...props }) => (
  <input className={controlClass(className)} {...props} />
);

export const Select = ({ className = '', children, ...props }) => (
  <select className={controlClass(className)} {...props}>
    {children}
  </select>
);

export const Textarea = ({ className = '', rows = 5, ...props }) => (
  <textarea rows={rows} className={controlClass(className)} {...props} />
);

const BADGE_TONES = {
  neutral: 'bg-slate-100 text-slate-700',
  info: 'bg-blue-100 text-blue-800',
  success: 'bg-emerald-100 text-emerald-800',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-800',
  indigo: 'bg-indigo-100 text-indigo-800',
};

export const Badge = ({ tone = 'neutral', children, className = '' }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium
      ${BADGE_TONES[tone]} ${className}`}
  >
    {children}
  </span>
);

export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center rounded-lg bg-slate-50 px-6 py-12 text-center">
    {Icon && <Icon size={40} className="mb-3 text-slate-300" />}
    <p className="font-medium text-slate-700">{title}</p>
    {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Alert = ({ tone = 'info', title, children }) => {
  const tones = {
    info: 'border-blue-200 bg-blue-50 text-blue-900',
    warning: 'border-amber-200 bg-amber-50 text-amber-900',
    danger: 'border-red-200 bg-red-50 text-red-900',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  };
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm ${tones[tone]}`}>
      {title && <p className="font-semibold">{title}</p>}
      {children}
    </div>
  );
};

/** Horizontally scrollable on small screens rather than squashing columns. */
export const Table = ({ headers, children }) => (
  <div className="-mx-6 overflow-x-auto px-6">
    <table className="w-full min-w-[640px] text-left text-sm">
      <thead>
        <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          {headers.map((header, index) => (
            <th key={index} className="whitespace-nowrap px-3 py-2 font-medium">{header}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">{children}</tbody>
    </table>
  </div>
);

export const Modal = ({ open, title, onClose, children, footer, wide = false }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4">
      <div className={`mt-8 w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} rounded-xl bg-white shadow-xl`}>
        <header className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </header>
        <div className="px-6 py-5">{children}</div>
        {footer && (
          <footer className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">{footer}</footer>
        )}
      </div>
    </div>
  );
};

export const StatCard = ({ icon: Icon, label, value, tone = 'indigo' }) => {
  const tones = {
    indigo: 'from-indigo-500 to-indigo-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600',
    sky: 'from-sky-500 to-sky-600',
    rose: 'from-rose-500 to-rose-600',
  };
  return (
    <div className={`rounded-xl bg-gradient-to-br ${tones[tone]} p-5 text-white shadow-sm`}>
      {Icon && <Icon size={24} className="mb-2 opacity-80" />}
      <p className="text-xs font-medium uppercase tracking-wide opacity-90">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </div>
  );
};
